import React, { useState, useEffect } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { PlusCircle, Check } from "lucide-react";
import { DateTimeCard } from "@/components/cash-entry/DateTimeCard";
import { CashEntryForm } from "@/components/cash-entry/CashEntryForm";
import { BancontactQRSection } from "@/components/registration/BancontactQRSection";

const CashEntryPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [amount, setAmount] = useState("");
  const [nature, setNature] = useState("");
  const [payerLastName, setPayerLastName] = useState("");
  const [payerFirstName, setPayerFirstName] = useState("");
  const [paymentType, setPaymentType] = useState("");

  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [dateModified, setDateModified] = useState(false);

  useEffect(() => {
    const now = new Date();
    setCustomDate(now.toISOString().split("T")[0]);
    setCustomTime(now.toTimeString().slice(0, 5));
  }, []);

  useEffect(() => {
    if (dateModified) return;
    const interval = setInterval(() => {
      const now = new Date();
      setCustomDate(now.toISOString().split("T")[0]);
      setCustomTime(now.toTimeString().slice(0, 5));
    }, 1000);
    return () => clearInterval(interval);
  }, [dateModified]);

  const handleDateChange = (value) => {
    setCustomDate(value);
    setDateModified(value !== new Date().toISOString().split("T")[0]);
  };
  const handleTimeChange = (value) => { setCustomTime(value); setDateModified(true); };
  const resetDateToNow = () => {
    const now = new Date();
    setCustomDate(now.toISOString().split("T")[0]);
    setCustomTime(now.toTimeString().slice(0, 5));
    setDateModified(false);
  };

  const resetForm = () => {
    setAmount(""); setNature(""); setPayerLastName(""); setPayerFirstName(""); setPaymentType(""); setError(null);
    resetDateToNow();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(false);
    if (!amount || parseFloat(amount) <= 0) { setError("Veuillez entrer un montant valide"); return; }
    if (!nature.trim()) { setError("Veuillez entrer la nature du paiement"); return; }
    if (!payerLastName.trim()) { setError("Veuillez entrer le nom du payeur"); return; }
    if (!payerFirstName.trim()) { setError("Veuillez entrer le prénom du payeur"); return; }
    if (!paymentType) { setError("Veuillez sélectionner un mode de paiement"); return; }
    setLoading(true);
    try {
      await apiClient.post("/payments/manual", {
        amount: parseFloat(amount), nature: nature.trim(),
        payer_last_name: payerLastName.trim(), payer_first_name: payerFirstName.trim(),
        payment_type: paymentType,
        custom_date: dateModified ? customDate : null, custom_time: dateModified ? customTime : null,
      });
      setSuccess(true); resetForm();
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error("Error creating payment entry:", err);
      setError(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto" data-testid="cash-entry-page">
      <div className="mb-6 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-[#162d3d] flex items-center justify-center gap-2">
          <PlusCircle size={28} className="text-[#22c55e]" /> Paiement hors abonnement
        </h1>
        <p className="text-[#8fa4b5] mt-1">Enregistrer un encaissement (cours privé, vente, etc.)</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <DateTimeCard
          customDate={customDate} customTime={customTime} dateModified={dateModified}
          userName={user?.name || user?.email || "Coach"}
          onDateChange={handleDateChange} onTimeChange={handleTimeChange} onReset={resetDateToNow}
        />

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="success-message">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"><Check size={18} className="text-green-600" /></div>
            <div>
              <p className="font-semibold text-green-800">Entrée enregistrée avec succès !</p>
              <p className="text-sm text-green-600">Un email de notification a été envoyé.</p>
            </div>
            <button onClick={() => setSuccess(false)} className="ml-auto text-green-400 hover:text-green-600 text-xs flex-shrink-0" data-testid="dismiss-success">Fermer</button>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700" data-testid="error-message">{error}</div>}

        <CashEntryForm
          amount={amount} nature={nature} payerLastName={payerLastName} payerFirstName={payerFirstName} paymentType={paymentType} loading={loading}
          onAmountChange={setAmount} onNatureChange={setNature} onLastNameChange={setPayerLastName} onFirstNameChange={setPayerFirstName} onPaymentTypeChange={setPaymentType}
          onSubmit={handleSubmit}
        />

        <div className="mt-6">
          <BancontactQRSection />
        </div>

        <p className="text-center text-sm text-[#8fa4b5] mt-4">
          Une notification sera envoyée à sante@bhealth.be lors de la validation.
        </p>
      </div>
    </div>
  );
};

export default CashEntryPage;
